import { SgConnectEnvironment } from 'services/SgConnect/SgConnectEnvironment';
import { SGDashboard } from 'services/externalLinksService';

export enum Environment {
    Test = 'Test',
    Local = 'Local',
    Development = 'Development',
    Homologation = 'Homologation',
    Preview = 'Preview',
    Production = 'Production',
}

const allEnvs: Record<Environment, string[]> = {
    [Environment.Test]: [],
    [Environment.Local]: [
        'local.fr.world.socgen',
        'localhost',
    ],
    [Environment.Development]: [
        'subscription-dev.sgmarkets.com',
        'subscription-dev.azureedge.net',
    ],
    [Environment.Homologation]: [
        'subscription-uat.sgmarkets.com',
        'subscription-uat.azureedge.net',
    ],
    [Environment.Preview]: [
        'subscription-preview.sgmarkets.com',
        'subscription-preview.azureedge.net',
    ],
    [Environment.Production]: [
        'subscription.sgmarkets.com',
        'subscription.azureedge.net',
    ],
};

export const getEnvironment = (): Environment => {
    if ((window as any)?.vitest
        || typeof window === 'undefined'
        || !window.location.hostname) {
        return Environment.Test;
    }

    const env = Object.values(Environment)
        .find(x => allEnvs[x].includes(window.location.hostname));

    if (env) {
        return env;
    }

    throw new Error(`Can't find environment from hostname ${window.location.hostname}`);
};

interface IConfiguration {
    baseApiUrls: {
        subscription: string;
    },
    externalLinks: {
        requestAccessUrl: string;
        sgDashboard: SGDashboard;
        researchContract: string;
        iam: string;
    },
    authorizationEndpoint: string;
    clientId: string;
    scope: string;
    sgConnectEnvironment: SgConnectEnvironment;
    cdnSharedBaseUrl: string;
    projectName: string;
    InsightTemplateId: number;
    widgetCdnBaseUrl: string;
    requiredPermission: string;
    sgMonitoringRumUrl: string;
}

const defaultConfiguration: IConfiguration = {
    baseApiUrls: {
        subscription: 'https://pretrade-api-subscription-dev.sgmpinsight.dev.euw.gbis.sg-azure.com',
        //subscription: 'https://localhost:44321',
    },
    externalLinks: {
        requestAccessUrl: 'https://shared-uat.sgmarkets.com/unauthorized/?redirectUri=https://subscription-dev.sgmarkets.com',
        sgDashboard: {
            url: 'https://sg-dashboard-uat2.fr.world.socgen',
            boards: {
                contactReadership: 19558,
            },
            selectors: {
                icSingleContactPicker: 18,
                icMultiContactPicker: 3686,
            },
        },
        researchContract: 'https://researchcontract-dev.sgmarkets.world.socgen',
        iam: 'https://iam-hom.sgmarkets.com/home',
    },
    clientId: '089fd343-11d8-405f-8445-a744daa4ddfa',
    authorizationEndpoint: 'https://sgconnect-hom.fr.world.socgen/sgconnect',
    scope: [
        'openid',
        'profile',
        'mail',
        'api.pretrade-insight.v1',
        'api.subscribe-publication.v1',
        'api.subscribe-publication.v1-front',
        'api.idy-widgets.request-access',
    ].join(' '),
    sgConnectEnvironment: SgConnectEnvironment.Homologation,
    cdnSharedBaseUrl: 'https://shared-uat.fr.world.socgen',
    projectName: 'SGMI',
    InsightTemplateId: 1,
    widgetCdnBaseUrl: 'https://sgwt-widgets-dev.sgmarkets.com',
    requiredPermission: 'SGM_DEFAULT',
    sgMonitoringRumUrl: 'https://insight-realm-apm-hom.fleet.uat.sgmonitoring.dev.euw.gbis.sg-azure.com',
};

const getConfigurationPerEnvironment = (): IConfiguration => {
    const env = getEnvironment();

    switch (env) {
        case Environment.Test:
            return {
                ...defaultConfiguration,
                baseApiUrls: {
                    ...defaultConfiguration.baseApiUrls,
                    subscription: 'https://localhost',
                },
            };
        case Environment.Local:
            return defaultConfiguration;
        case Environment.Development:
            return {
                ...defaultConfiguration,
                baseApiUrls: {
                    ...defaultConfiguration.baseApiUrls,
                    subscription: 'https://pretrade-api-subscription-dev.sgmpinsight.dev.euw.gbis.sg-azure.com',
                },
                externalLinks: {
                    ...defaultConfiguration.externalLinks,
                    requestAccessUrl: 'https://shared-uat.sgmarkets.com/unauthorized/?redirectUri=https://subscription-dev.sgmarkets.com',
                },
            };

        case Environment.Homologation:
            return {
                ...defaultConfiguration,
                baseApiUrls: {
                    ...defaultConfiguration.baseApiUrls,
                    subscription: 'https://api-z-dev.sgmarkets.com/uat/subscribe-publication/v1',
                },
                externalLinks: {
                    ...defaultConfiguration.externalLinks,
                    requestAccessUrl: 'https://shared-uat.sgmarkets.com/unauthorized/?redirectUri=https://subscription-uat.sgmarkets.com',
                    researchContract: 'https://researchcontract-uat.sgmarkets.world.socgen',
                },
            };

        case Environment.Preview:
        case Environment.Production:
            return {
                ...defaultConfiguration,
                baseApiUrls: {
                    ...defaultConfiguration.baseApiUrls,
                    subscription: 'https://api-z.sgmarkets.com/services/insight/subscription/private/v1',
                },
                externalLinks: {
                    ...defaultConfiguration.externalLinks,
                    requestAccessUrl: 'https://shared.sgmarkets.com/unauthorized/?redirectUri=https://subscription.sgmarkets.com',
                    sgDashboard: {
                        url: 'https://dashboard.sgmarkets.world.socgen',
                        boards: {
                            contactReadership: 71790,
                        },
                        selectors: {
                            icSingleContactPicker: 614,
                            icMultiContactPicker: 5807,
                        },
                    },
                    researchContract: 'https://researchcontract.sgmarkets.world.socgen',
                    iam: 'https://iam.sgmarkets.com/home',
                },
                clientId: 'db45fdf7-a80b-4fb2-ad00-fe9b0f4c9d74',
                authorizationEndpoint: 'https://sso.sgmarkets.com/sgconnect/oauth2/authorize',
                sgConnectEnvironment: SgConnectEnvironment.Production,
                cdnSharedBaseUrl: 'https://shared.sgmarkets.com',
                widgetCdnBaseUrl: 'https://sgwt-cdn-widgets.sgmarkets.com',
                sgMonitoringRumUrl: 'https://t-monitoring-rum.sgmarkets.com/rum/540a239031b64d7fbe34fe54fef041db',
            };
    }
};

export const configuration = getConfigurationPerEnvironment();
